import React, { useState, ChangeEvent, useEffect } from 'react';
import {
  Button,
  DialogActions,
  TextField,
  Typography,
  Box,
  Divider,
  Stack,
} from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { theme } from '@/theme/theme';
import { GenericDialog } from '@/components/shared/GenericDialog';

type EditNoteDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: (note: string) => Promise<void>;
  initialNote: string | undefined | null;
  title?: string;
  maxLength?: number;
};

export const EditNoteDialog = ({
  open,
  onClose,
  onSave,
  initialNote,
  title = 'Edit Note',
  maxLength = 200,
}: EditNoteDialogProps) => {
  const [note, setNote] = useState<string>(initialNote || '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setNote(initialNote || '');
  }, [initialNote, open]);

  const handleNoteChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNote(event.target.value);
  };

  const handleRemoveNote = () => {
    setNote('');
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(note);
      onClose();
    } catch (error) {
      console.error('Failed to save note:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setNote(initialNote || '');
    onClose();
  };

  return (
    <GenericDialog
      open={open}
      onClose={handleClose}
      title={title}
      id="edit-note-dialog-title"
      maxWidth="sm"
      paperStyles={{ borderRadius: 2 }}
      minWidth="auto"
      content={
        <>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" sx={{ color: theme.palette.primary.dark }}>
              Write your note here
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, marginLeft: 1 }}>
              (max {maxLength} characters allowed)
            </Typography>
          </Box>
          <TextField
            margin="dense"
            id="note"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={note}
            onChange={handleNoteChange}
            inputProps={{ maxLength }}
            sx={{
              '& .MuiOutlinedInput-root': {
                minHeight: 'auto',
              },
              '& .MuiOutlinedInput-input': {
                resize: 'none',
                overflow: 'auto !important',
              },
            }}
          />
        </>
      }
      actions={
        <>
          <Divider />
          <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
            <Stack direction="row" justifyContent="flex-start" spacing={2} sx={{ mx: '12px' }}>
              <Button
                onClick={handleRemoveNote}
                color="primary"
                variant="text"
                startIcon={<DeleteOutlinedIcon />}
                sx={{ borderRadius: 2, color: theme.palette.primary.dark, ml: 0 }}
              >
                Remove Note
              </Button>
            </Stack>
            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Button
                onClick={handleClose}
                color="primary"
                variant="outlined"
                sx={{ borderRadius: 2 }}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                color="primary"
                variant="contained"
                sx={{ borderRadius: 2, background: theme.palette.primary.dark, marginRight: '12px' }}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </Stack>
          </DialogActions>
        </>
      }
      actionsStyles={{ p: 0 }}
    />
  );
};

